import { ChatEmoji, ChatEmojiCategory, ChatMessagePart } from '../model/chat-emoji';
import { getEmojiKey } from './chat-emoji-key';

/**
 * Indexes emoji catalog entries for token parsing and reaction display.
 */
export class ChatEmojiLookup
{
  private readonly emojiByKey = new Map<string, ChatEmoji>();
  private readonly emojiByInsertText = new Map<string, ChatEmoji>();
  private readonly emojiByChar = new Map<string, ChatEmoji>();
  private emojiCharsByLength: string[] = [];

  public static getInsertText(emoji: ChatEmoji): string
  {
    if (emoji.id)
    {
      return `:${emoji.id}:`;
    }

    return emoji.char ?? '';
  }

  public build(categories: ChatEmojiCategory[]): void
  {
    this.emojiByKey.clear();
    this.emojiByInsertText.clear();
    this.emojiByChar.clear();
    const chars = new Set<string>();

    categories.forEach((category) =>
    {
      category.emojis.forEach((emoji) =>
      {
        const key = getEmojiKey(emoji);
        if (key)
        {
          this.emojiByKey.set(key, emoji);
        }

        if (emoji.id)
        {
          this.emojiByInsertText.set(`:${emoji.id}:`, emoji);
        }

        if (emoji.char)
        {
          this.emojiByChar.set(emoji.char, emoji);
          chars.add(emoji.char);
        }
      });
    });

    this.emojiCharsByLength = [...chars].sort((a, b) => b.length - a.length);
  }

  public getByKey(key: string): ChatEmoji | undefined
  {
    return this.emojiByKey.get(key);
  }

  public resolveStoredKeys(keys: string[]): ChatEmoji[]
  {
    return keys
      .map((key) => this.emojiByKey.get(key))
      .filter((emoji): emoji is ChatEmoji => !!emoji);
  }

  public parseMessageParts(text: string): ChatMessagePart[]
  {
    const normalized = text.replace(/\r\n/g, '\n');
    const parts: ChatMessagePart[] = [];
    let buffer = '';
    let index = 0;

    const flushText = (): void =>
    {
      if (buffer)
      {
        parts.push({ type: 'text', value: buffer });
        buffer = '';
      }
    };

    while (index < normalized.length)
    {
      if (normalized[index] === ':')
      {
        const closeIndex = normalized.indexOf(':', index + 1);
        if (closeIndex !== -1)
        {
          const token = normalized.slice(index, closeIndex + 1);
          const customEmoji = this.emojiByInsertText.get(token);
          if (customEmoji)
          {
            flushText();
            parts.push({ type: 'emoji', emoji: customEmoji });
            index = closeIndex + 1;
            continue;
          }
        }
      }

      let matchedEmoji: ChatEmoji | undefined;
      let matchedLength = 0;
      for (const char of this.emojiCharsByLength)
      {
        if (normalized.startsWith(char, index))
        {
          matchedEmoji = this.emojiByChar.get(char);
          if (matchedEmoji)
          {
            matchedLength = char.length;
            break;
          }
        }
      }

      if (matchedEmoji && matchedLength > 0)
      {
        flushText();
        parts.push({ type: 'emoji', emoji: matchedEmoji });
        index += matchedLength;
        continue;
      }

      buffer += normalized[index];
      index++;
    }

    flushText();
    return parts;
  }
}
